import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Search, ChevronDown, Mail, Phone, MessageSquare, LifeBuoy, Link2 } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { HELP_CATEGORIES, HELP_FAQS, searchFaqs } from "@/data/faqs";

const FaqItem = ({ item, open, onToggle }) => {
  const copyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/help#${item.slug}`)
      .then(() => toast.success("Link copied to clipboard"))
      .catch(() => toast.error("Could not copy link"));
  };
  return (
    <div id={item.slug} className={`rounded-2xl border bg-white/[0.02] overflow-hidden scroll-mt-24 transition-colors ${open ? "border-cyan-300/30" : "border-white/10"}`}>
      <button onClick={onToggle} data-testid={`faq-${item.slug}`}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="text-[15px] text-white/85">{item.q}</span>
        <ChevronDown size={16} className={`shrink-0 text-cyan-300 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-sm leading-relaxed text-white/55 font-light">{item.a}</p>
          <button onClick={copyLink} data-testid={`faq-copy-${item.slug}`}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] text-white/50 hover:text-white hover:border-white/25 transition-colors">
            <Link2 size={11} /> Copy link to this answer
          </button>
        </div>
      )}
    </div>
  );
};

export default function HelpCenter() {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("billing");
  const [openSlug, setOpenSlug] = useState(null);

  useEffect(() => {
    document.title = "Help Center — ArcaCentum, Inc.";
    const slug = (location.hash || "").replace(/^#/, "");
    const target = slug && HELP_FAQS.find((f) => f.slug === slug);
    if (target) {
      setCategory(target.c);
      setOpenSlug(target.slug);
      setTimeout(() => document.getElementById(target.slug)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } else {
      window.scrollTo(0, 0);
    }
    return () => { document.title = "ArcaCentum — AI Technology & Software Products"; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (slug) => {
    const next = openSlug === slug ? null : slug;
    setOpenSlug(next);
    window.history.replaceState(null, "", next ? `/help#${next}` : "/help");
  };

  const searching = query.trim().length > 0;
  const results = useMemo(
    () => (searching ? searchFaqs(query) : HELP_FAQS.filter((f) => f.c === category)),
    [query, category, searching]
  );

  return (
    <div data-testid="help-center-page" className="min-h-screen bg-[#0a0a0f] text-white">
      <header className="border-b border-white/[0.06]">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="help-home-link" className="flex items-center gap-2.5">
            <LogoMark size={26} />
            <span className="font-display text-base font-600 text-white">ArcaCentum</span>
          </Link>
          <nav className="flex gap-5 text-xs text-white/50">
            <Link to="/credit" className="hover:text-white transition-colors">ArcaCentum Credit</Link>
            <Link to="/privacy" className="hidden sm:block hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hidden sm:block hover:text-white transition-colors">Terms</Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-14 sm:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/80">Help Center</p>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-600 tracking-[-0.02em]">How can we help?</h1>

        <div className="relative mt-8 max-w-xl">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpenSlug(null); }}
            placeholder="Search questions… (e.g. cancel, refund, password)"
            data-testid="help-search-input"
            className="w-full rounded-full border border-white/10 bg-white/[0.03] pl-11 pr-5 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-cyan-300/40 transition-colors"
          />
        </div>

        {!searching && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="help-categories">
            {HELP_CATEGORIES.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => { setCategory(id); setOpenSlug(null); }} data-testid={`help-category-${id}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs transition-colors ${
                  category === id
                    ? "border-cyan-300/50 bg-cyan-300/10 text-white"
                    : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white hover:border-white/25"
                }`}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8 space-y-3" data-testid="help-results">
          {searching && (
            <p className="text-sm text-white/45" data-testid="help-search-count">
              {results.length} result{results.length === 1 ? "" : "s"} for “{query.trim()}”
            </p>
          )}
          {results.map((item) => (
            <FaqItem key={item.slug} item={item} open={openSlug === item.slug} onToggle={() => toggle(item.slug)} />
          ))}
          {results.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-10 text-center" data-testid="help-no-results">
              <p className="text-white/70">No answers matched your search.</p>
              <p className="mt-2 text-sm text-white/45">Try different keywords, or reach out below — we're happy to help.</p>
            </div>
          )}
        </div>

        <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8" data-testid="help-contact-card">
          <h2 className="font-display text-lg sm:text-xl font-600 text-white flex items-center gap-2">
            <LifeBuoy size={18} className="text-cyan-300" /> Still need help?
          </h2>
          <p className="mt-2 text-sm text-white/55 font-light">Our team responds to most inquiries within 1–2 business days.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <a href="mailto:support@arcacentum.com" data-testid="help-email-link"
              className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-sm text-white/70 hover:text-white hover:border-white/25 transition-colors">
              <Mail size={15} className="text-cyan-300 shrink-0" /> support@arcacentum.com
            </a>
            <a href="tel:+18889775190" data-testid="help-phone-link"
              className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-sm text-white/70 hover:text-white hover:border-white/25 transition-colors">
              <Phone size={15} className="text-cyan-300 shrink-0" /> (888) 977-5190
            </a>
            <a href="/#contact" data-testid="help-contact-form-link"
              className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-sm text-white/70 hover:text-white hover:border-white/25 transition-colors">
              <MessageSquare size={15} className="text-cyan-300 shrink-0" /> Contact form
            </a>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] py-8">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
          <span>© {new Date().getFullYear()} ArcaCentum, Inc. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms of Service</Link>
            <Link to="/refund-policy" className="hover:text-white/60 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
